import type { LucideIcon } from "lucide-react";
import {
  BookOpenIcon,
  BookOpenTextIcon,
  BrainIcon,
  CloudRainIcon,
  CompassIcon,
  GlobeIcon,
  HeartHandshakeIcon,
  LibraryIcon,
  ListChecksIcon,
  MessageCircleIcon,
  MessageSquareQuoteIcon,
  MessagesSquareIcon,
  PrinterIcon,
  SchoolIcon,
  ShieldIcon,
  SmartphoneIcon,
  SunriseIcon,
  TriangleAlertIcon,
} from "lucide-react";

export const heroBenefits = ["6 Digital Volumes", "Question-Based Lessons", "5 Exclusive Bonuses"];

export const problemStories = [
  "Your child may know Noah's ark.",
  "They may remember David and Goliath.",
  "They may even recite entire passages of Scripture.",
];

export const problemQuestions = [
  "How do you know God exists?",
  "Why should anyone trust the Bible?",
  "What evidence is there that Jesus rose from the dead?",
  "If God is good, why does He allow suffering?",
];

// Paste real interior page screenshots into src (4–6 slots).
export const interiorPages = [
  { label: "Lesson page", detail: "Volume 1 — Does God Really Exist?", src: "" },
  { label: "Explanation page", detail: "Volume 2 — Can We Really Trust the Bible?", src: "" },
  { label: "Reasoning page", detail: "Volume 3 — Did Jesus Really Rise from the Dead?", src: "" },
  { label: "Conversation page", detail: "Volume 4 — If God Is Good, Why Is There Suffering?", src: "" },
  { label: "Discussion page", detail: "Volume 6 — How Can I Explain What I Believe?", src: "" },
];

export const valueBlocks: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: LibraryIcon,
    title: "6 Complete Digital Volumes",
    text: "Explore important questions about God, Scripture, Jesus, suffering, and the foundations of Christian belief.",
  },
  {
    icon: MessagesSquareIcon,
    title: "Question-Based Lessons",
    text: "Question-based explanations, thoughtful examples, and meaningful family discussion activities.",
  },
  {
    icon: SmartphoneIcon,
    title: "Instant Digital Access",
    text: "Open your collection on a phone, tablet, or computer as soon as your purchase is complete.",
  },
  {
    icon: PrinterIcon,
    title: "Flexible & Printable",
    text: "Explore the questions that matter most to your child, at your family's own pace.",
  },
];

export const personaItems = [
  "You're worried your child knows Bible stories but may struggle to explain why Christians believe them.",
  "You want your child to be better prepared when someone asks difficult questions about God or the Bible.",
  "You've been asked a question about faith that you didn't know how to answer.",
  "You believe Sunday School matters, but you want your child's understanding to go deeper than memorization.",
  "You want to begin preparing your child for the questions that often come with adolescence.",
];

export const methodSteps: { step: string; icon: LucideIcon; title: string; lines: string[] }[] = [
  {
    step: "01",
    icon: BookOpenIcon,
    title: "Guided Study",
    lines: [
      "Begin with a question your child genuinely wants to understand.",
      "Each lesson introduces important ideas in clear, age-appropriate language.",
    ],
  },
  {
    step: "02",
    icon: MessageCircleIcon,
    title: "Natural Conversations",
    lines: [
      "Use guided discussion questions to turn difficult topics into meaningful family conversations.",
      "Let your child ask, wonder, and explain what they're thinking.",
    ],
  },
  {
    step: "03",
    icon: BrainIcon,
    title: "Christian Critical Thinking",
    lines: [
      "Explore reasons, arguments, and explanations.",
      "Help your child move beyond simply repeating an answer.",
    ],
  },
  {
    step: "04",
    icon: HeartHandshakeIcon,
    title: "Faith in Real Life",
    lines: [
      "Connect what they're learning to school, friendships, family life, and the questions they may encounter outside church.",
    ],
  },
];

export const volumes: {
  number: number;
  title: string;
  text: string;
  color: string;
  icon: LucideIcon;
}[] = [
  {
    number: 1,
    title: "Does God Really Exist?",
    text: "Explore the reasons Christians believe in God, questions about the origin of the universe, creation, science, and morality.",
    color: "#2457C5",
    icon: GlobeIcon,
  },
  {
    number: 2,
    title: "Can We Really Trust the Bible?",
    text: "Help your child understand how Scripture was written, preserved, and translated—and how to approach difficult questions about its reliability.",
    color: "#1D6F67",
    icon: BookOpenTextIcon,
  },
  {
    number: 3,
    title: "Did Jesus Really Rise from the Dead?",
    text: "Explore important historical questions about Jesus, his crucifixion, the resurrection accounts, and the reasons Christians believe in the resurrection.",
    color: "#B4462A",
    icon: SunriseIcon,
  },
  {
    number: 4,
    title: "If God Is Good, Why Is There Suffering?",
    text: "Help children think through difficult questions about pain, evil, sadness, unanswered prayers, and God's goodness.",
    color: "#5A4A9B",
    icon: CloudRainIcon,
  },
  {
    number: 5,
    title: "What If Someone Challenges My Faith?",
    text: "Explore common objections, disagreements, and situations children may encounter at school or with friends.",
    color: "#A0650E",
    icon: ShieldIcon,
  },
  {
    number: 6,
    title: "How Can I Explain What I Believe?",
    text: "Help children put their beliefs into their own words, ask thoughtful questions, and participate in respectful conversations.",
    color: "#2C7A4B",
    icon: MessageSquareQuoteIcon,
  },
];

export const collectionBenefits = [
  "A deeper understanding of faith.",
  "Greater clarity when difficult questions arise.",
  "Meaningful conversations at home.",
  "More thoughtful reasoning.",
  "A stronger foundation for the years ahead.",
];

export const bonuses: {
  number: number;
  title: string;
  coverTitle: string;
  text: string;
  color: string;
  icon: LucideIcon;
}[] = [
  {
    number: 1,
    title: "The Christian Parent's Conversation Guide",
    coverTitle: "Parent's Conversation Guide",
    text: "Learn how to begin meaningful conversations, respond to unexpected questions, and create an environment where your child feels comfortable talking about faith.",
    color: "#13285C",
    icon: MessagesSquareIcon,
  },
  {
    number: 2,
    title: "7 Parenting Mistakes That Can Weaken Faith Conversations",
    coverTitle: "7 Parenting Mistakes",
    text: "Discover seven common communication mistakes and practical ways to replace dismissive answers with more thoughtful responses.",
    color: "#B4462A",
    icon: TriangleAlertIcon,
  },
  {
    number: 3,
    title: "Preparing Their Faith for the Teen Years",
    coverTitle: "Faith for the Teen Years",
    text: "Support your child through changing friendships, growing independence, cultural influences, and more complex questions about faith.",
    color: "#1D6F67",
    icon: CompassIcon,
  },
  {
    number: 4,
    title: "When School Challenges Their Beliefs",
    coverTitle: "When School Challenges Beliefs",
    text: "Explore common school-related scenarios and parent-friendly discussion examples to help your child process questions and disagreements.",
    color: "#5A4A9B",
    icon: SchoolIcon,
  },
  {
    number: 5,
    title: "The Tough Questions Quick-Answer Guide",
    coverTitle: "Tough Questions Quick Answers",
    text: "Get 40 common questions with clear starting explanations and references to the main collection for deeper exploration.",
    color: "#2457C5",
    icon: ListChecksIcon,
  },
];

export const includedItems = [
  "Volume 1 — Does God Really Exist?",
  "Volume 2 — Can We Really Trust the Bible?",
  "Volume 3 — Did Jesus Really Rise from the Dead?",
  "Volume 4 — If God Is Good, Why Is There Suffering?",
  "Volume 5 — What If Someone Challenges My Faith?",
  "Volume 6 — How Can I Explain What I Believe?",
  "Question-Based Lessons",
  "Bonus 1 — The Christian Parent's Conversation Guide",
  "Bonus 2 — 7 Parenting Mistakes That Can Weaken Faith Conversations",
  "Bonus 3 — Preparing Their Faith for the Teen Years",
  "Bonus 4 — When School Challenges Their Beliefs",
  "Bonus 5 — The Tough Questions Quick-Answer Guide",
  "Instant Digital Access",
  "Printable Family-Friendly Materials",
];

// Fill in real parent reviews when available. Empty entries render as placeholders.
export const testimonials = [
  {
    quote:
      "My son has always asked difficult questions about God, and I never knew quite what to say. What I love about this approach is that it helps us explore those questions together instead of just giving him answers to memorize. He's learning how to explain why he believes.",
    name: "Emily R.",
    detail: "Mom of a 9-year-old boy",
    photo: "",
  },
  {
    quote:
      "I realized I was teaching my son what to say about faith, but not how to think through it. The lessons gave us a way to slow down, ask better questions, and have real conversations. He's becoming more comfortable talking about his beliefs without feeling like he has to win an argument.",
    name: "Michael D.",
    detail: "Dad of an 11-year-old boy",
    photo: "",
  },
  {
    quote:
      "I used to worry whenever my daughter questioned something about Christianity. I thought her doubts meant she was losing her faith. Now I see those questions as opportunities to listen and connect. Our conversations feel so much calmer and more meaningful.",
    name: "Jessica S.",
    detail: "Single mom",
    photo: "",
  },
];

export const faqs = [
  {
    q: "Is this just another Bible story collection?",
    a: "No. WHY WE BELIEVE goes beyond retelling familiar Bible stories. It helps children explore questions about Christian beliefs, the reasons behind them, and how to discuss those ideas thoughtfully.",
  },
  {
    q: "What ages is it designed for?",
    a: "The main collection is designed for children ages 8–14. Parents can adapt the pace and depth of each conversation to their child's age and understanding.",
  },
  {
    q: "Do I need a background in theology to use it?",
    a: "No. The materials are organized to help families explore important questions together. The bonus guides provide additional support for parents.",
  },
  {
    q: "Does this replace Sunday School or church?",
    a: "No. WHY WE BELIEVE complements the Christian education children receive at church by focusing on the reasoning and questions behind their beliefs.",
  },
  {
    q: "Do we have to complete all 52 lessons?",
    a: "No. There is no required schedule. You can begin with the questions your child is asking right now and explore other lessons whenever your family is ready.",
  },
  {
    q: "Are all five bonuses included in the $27 price?",
    a: "Yes. One purchase includes all six main volumes, all 52 lessons, and all five bonus guides.",
  },
  {
    q: "Is this a physical product?",
    a: "No. This is a digital product. You'll receive digital resources that you can access on compatible devices and print for personal family use.",
  },
  {
    q: "How will I receive access?",
    a: "After your payment is confirmed, you'll receive access instructions through the email address provided at checkout.",
  },
  {
    q: "Is this a subscription?",
    a: "No. It's a one-time purchase with no recurring subscription charges.",
  },
  {
    q: "What if I'm not satisfied?",
    a: "Your purchase is protected by our 7-Day Money-Back Guarantee. You can request a refund within seven days by contacting our support team.",
  },
];
